import api from './api';

/**
 * Simulation run control.
 *
 * The server is authoritative for phase and score — every call here returns the full
 * run state, and the page renders whatever comes back rather than keeping its own copy.
 * That is what makes the same screens work later when a room full of participants is
 * also mutating the run.
 *
 * `advancePhase` sends `from` so the server can compare-and-set: if two clicks race, the
 * second gets a 409 carrying the true state instead of applying the transition twice.
 */
const simulationRunService = {
  /** POST /simulations/:id/runs */
  start: (simulationId, { mode = 'rehearsal', sessionId = null } = {}) =>
    api.post(`/simulations/${simulationId}/runs`, { mode, sessionId }),

  /** GET /simulation-runs/:runId */
  get: (runId) => api.get(`/simulation-runs/${runId}`),

  /**
   * GET /simulation-runs/active?sessionId=... — the newest non-closed run for a session.
   * Resolves with { run: null } when there is nothing to rejoin; that is a normal answer,
   * so callers should check `run` rather than treat it as an error.
   */
  activeForSession: (sessionId) =>
    api.get('/simulation-runs/active', { sessionId }),

  /** POST /simulation-runs/:runId/assign — round-robin unless explicit assignments given. */
  assign: (runId, assignments = null) =>
    api.post(`/simulation-runs/${runId}/assign`, assignments ? { assignments } : {}),

  /** POST /simulation-runs/:runId/phase */
  advancePhase: (runId, { from, to, roundIndex }) =>
    api.post(`/simulation-runs/${runId}/phase`, { from, to, roundIndex }),

  /** POST /simulation-runs/:runId/commit — idempotent; safe to re-send. */
  commit: (runId, { functionKey, cardId }) =>
    api.post(`/simulation-runs/${runId}/commit`, { functionKey, cardId }),

  /**
   * POST /simulation-runs/:runId/resolve
   * `eventCardId` is the wildcard the trainer chose to fire this round — pass null
   * explicitly for "no event". Omitting the key falls back to the round's own default.
   */
  resolve: (runId, eventCardId = null) =>
    api.post(`/simulation-runs/${runId}/resolve`, { eventCardId }),

  /** POST /simulation-runs/:runId/reveal-angles — rehearsal only; never broadcast. */
  revealAngles: (runId, revealed = true) =>
    api.post(`/simulation-runs/${runId}/reveal-angles`, { revealed }),

  /** POST /simulation-runs/:runId/extend — more time on the negotiate/decide clock. */
  extend: (runId, seconds = 60) =>
    api.post(`/simulation-runs/${runId}/extend`, { seconds }),

  /** POST /simulation-runs/:runId/final-view — which part of the final debrief the room sees. */
  setFinalView: (runId, view) =>
    api.post(`/simulation-runs/${runId}/final-view`, { view }),

  /** POST /simulation-runs/:runId/whatif — computed and logged, never scored. */
  whatIf: (runId, { functionKey, altCardId }) =>
    api.post(`/simulation-runs/${runId}/whatif`, { functionKey, altCardId }),

  /** POST /simulation-runs/:runId/end */
  end: (runId) => api.post(`/simulation-runs/${runId}/end`),

  /**
   * GET /simulation-runs/:runId/projector
   * Projector-safe payload — structurally incapable of carrying a pre-reveal choice.
   */
  projector: (runId) => api.get(`/simulation-runs/${runId}/projector`),

  /**
   * GET /simulation-runs/:runId/report — the debrief PDF.
   * Fetched directly rather than through api.get, which parses JSON; a PDF is a blob.
   */
  downloadReport: async (runId) => {
    const base = process.env.REACT_APP_API_URL
      || (window.location.hostname === 'localhost' ? 'http://localhost:5000/api' : '/api');
    const token = localStorage.getItem('token') || sessionStorage.getItem('__ak_token');
    const res = await fetch(`${base}/simulation-runs/${runId}/report`, {
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const e = new Error(err.error || 'Could not build the report');
      e.userMessage = err.error;
      throw e;
    }
    const blob = await res.blob();
    const name = (res.headers.get('content-disposition') || '').match(/filename="([^"]+)"/)?.[1] || 'debrief.pdf';
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = name; document.body.appendChild(a); a.click();
    a.remove(); URL.revokeObjectURL(url);
    return { filename: name };
  },

  /** GET /simulation-runs/:runId/participant-view?functionKey= */
  participantView: (runId, functionKey) =>
    api.get(`/simulation-runs/${runId}/participant-view?functionKey=${encodeURIComponent(functionKey)}`),
};

export default simulationRunService;
